import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import cx from 'classnames'

import CompanyLogo from 'components/CompanyLogo'
import OnlineIndicator from 'components/OnlineIndicator'
import { FilterPage } from 'context/filtersContext'
import usePresenceSub from 'hooks/usePresenceSubscription'
import { ActivityGroupByAccountAndEndUsersBody, Insight } from 'modules/accountActivity/domain'
import ActivityCounts from 'sections/GroupedActivities/ActivityCounts'
import ExpandedActivities from 'sections/GroupedActivities/ExpandedActivities'
import FormattedActivity from 'sections/GroupedActivities/FormattedActivity'
import PortalTooltip from 'sections/GroupedActivities/PortalTooltip'
import { formatNumber } from 'utils/formatNumbers'
import { accountPath,rootPath } from 'utils/paths'
import { humanizedRelativeTime } from 'utils/timeStamp'

import utilityStyles from '../../../../styles/utility.module.css'
import styles from './styles.module.css'

const RECENT_ACTIVITY_THRESHOLD_MS = 60 * 60 * 1000

interface GroupedActivitiesFeedRowProps {
  insight: Insight
  expanded: boolean
  onChangeRowHeight: (height: number) => void
  onClickShowMoreContacts: () => void
  maxContactToShow: number
  getMaxActivitiesToShow: (contactIndex: number) => number
  onClickShowMoreActivities: (contactIndex: number) => void
  filterPage: FilterPage
}

const GroupedActivitiesFeedRow: React.FC<GroupedActivitiesFeedRowProps> = ({
  insight,
  expanded,
  onChangeRowHeight,
  onClickShowMoreContacts,
  maxContactToShow,
  getMaxActivitiesToShow,
  onClickShowMoreActivities,
  filterPage
}) => {
  const { accountData, companyData } = insight
  const body = insight.body as ActivityGroupByAccountAndEndUsersBody
  const contactList = body.activityGroupsByEndUser
  const endUserIds = body.endUserIds
  const numOtherContacts = contactList.length - 1
  const accountDisplayName = accountData?.name || companyData?.domain
  const { knownContacts, siteVisitors } = body.contactCounts
  const firstKnownContact = contactList.find(({ email, name }) => email !== null || name !== null)

  const history = useHistory()
  const recentlyActive = useMemo(() => {
    return contactList
      .filter(group => group.lastActivityAt > Date.now() - RECENT_ACTIVITY_THRESHOLD_MS)
      .map(group => group.endUserData)
  }, [contactList])
  const presentEndUserIds = usePresenceSub(insight.orgId, recentlyActive)
  const hasOnlineUser = endUserIds.filter(endUserId => presentEndUserIds.has(endUserId)).length > 0

  const handleClickAccount = useCallback(() => {
    history[filterPage === FilterPage.ACCOUNT_DETAILS ? 'replace' : 'push']({
      pathname: accountPath(insight.accountId),
      state: {
        accountData: insight.accountData,
        navigatedFrom: 'Account activity',
        navigatedFromUrl: rootPath
      },
      hash: history.location.hash
    })
  }, [history, insight.accountId, insight.accountData, filterPage])

  const [isCompanyNameTruncated, setIsCompanyNameTruncated] = useState(false)

  const handleMouseEnterCompanyName = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const target = e.currentTarget as HTMLButtonElement
      setIsCompanyNameTruncated(target.offsetWidth < target.scrollWidth)
    },
    []
  )

  return (
    <>
      <div>
        <div className={styles.cell}>
          <div className={styles.companyLogo} onClick={handleClickAccount}>
            <CompanyLogo company={{ ...companyData, name: accountDisplayName }} />
          </div>
          <div className={styles.firstContactAndCompany}>
            <div className={cx(styles.contactAndNumOthers, utilityStyles.semiBoldWeight)}>
              <span className={styles.contact}>
                {firstKnownContact?.name || firstKnownContact?.email || 'Site visitor'}
              </span>
              {numOtherContacts > 0 && (
                <span>
                  {' + '}
                  {numOtherContacts} {numOtherContacts === 1 ? 'other' : 'others'}
                </span>
              )}
            </div>
            <div className={styles.contactCountsAndCompanyName}>
              <span className={styles.contactCounts}>
                {[
                  knownContacts &&
                    `${formatNumber(knownContacts)} ${
                      knownContacts === 1 ? 'contact' : 'contacts'
                    }`,
                  siteVisitors &&
                    `${formatNumber(siteVisitors)} ${
                      siteVisitors === 1 ? 'site visitor' : 'site visitors'
                    }`
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
              <PortalTooltip
                className={styles.tooltip}
                tooltipClassName={cx({
                  [styles.tooltipHidden]: !isCompanyNameTruncated
                })}
                component={accountDisplayName}>
                <button
                  className={cx(styles.accountNameButton, {
                    [styles.disabledButton]: filterPage === FilterPage.ACCOUNT_DETAILS
                  })}
                  onClick={handleClickAccount}
                  onMouseEnter={handleMouseEnterCompanyName}>
                  {accountDisplayName}
                </button>
              </PortalTooltip>
            </div>
          </div>
        </div>
        <div className={styles.cell}>
          <ActivityCounts activityCounts={body.activityCounts} />
        </div>
        <div className={cx(styles.cell, styles.activityCell)}>
          <FormattedActivity
            activity={contactList[0].activityList[0]}
            displayName={contactList[0].name || contactList[0].email || 'Site visitor'}
            email={contactList[0].email}
          />
          <span className={styles.activityTime}>
            {hasOnlineUser ? (
              <div className={styles.onlineBadge}>
                <OnlineIndicator online={true} />
                <p>Online</p>
              </div>
            ) : (
              humanizedRelativeTime(insight.lastActivityAt)
            )}
          </span>
        </div>
      </div>
      <div className={styles.expandedActivities}>
        <ExpandedActivities
          insight={insight}
          expanded={expanded}
          contactList={contactList}
          onChangeRowHeight={onChangeRowHeight}
          maxContactToShow={maxContactToShow}
          onClickShowMoreContacts={onClickShowMoreContacts}
          getMaxActivitiesToShow={getMaxActivitiesToShow}
          onClickShowMoreActivities={onClickShowMoreActivities}
          companyData={companyData}
          presentEndUserIds={presentEndUserIds}
          onClickAccount={handleClickAccount}
          showLinkToAccountDetails={filterPage !== FilterPage.ACCOUNT_DETAILS}
        />
      </div>
    </>
  )
}

export default GroupedActivitiesFeedRow
